import {Outlet, ScrollRestoration, useLocation} from 'react-router-dom';
import React, {useEffect, useLayoutEffect, useState} from 'react';
import ReactGA from 'react-ga4';
import Footer from "../components/common/Footer/Footer";
import {MainNavProvider} from "../context/MainNavContext";
import NavMenu from "../components/Menu/Menu";
import {Message} from "../components/PrimereactComponents";
import {GA_TRACKING_ID, PAGE_WARNING} from "../Constants";
import MobileBottomNav from '../components/MobileBottomNav/MobileBottomNav';
import useDimensions from '../hooks/useDimensions';
import { useMainNav } from '../context/MainNavContext';
import { LocalizationLabelsProvider } from '../context/LocalizationLabelContext';

export interface FooterContextType {
    setLatestMeetingAt: React.Dispatch<React.SetStateAction<string>>;
    setLatestMeetingNumber: React.Dispatch<React.SetStateAction<string>>;
    setMunicipalityTimeZone: React.Dispatch<React.SetStateAction<string>>;
}

if (GA_TRACKING_ID) {
    ReactGA.initialize([{ trackingId: GA_TRACKING_ID }]);
}

const FooterContext = React.createContext<FooterContextType | null>(null);

export const useFooterContext = (): FooterContextType => {
    const context = React.useContext(FooterContext);
    if (context === null) {
        throw new Error('useFooterContext must be used within a FooterContext.Provider');
    }
    return context;
};


const ScrollToTop = ({ children }: { children?: React.ReactNode }) => {
    const location = useLocation();
    useLayoutEffect(() => {
        document.documentElement.scrollTo(0, 0);
    }, [location.pathname]);
    return children;
};

const Analytics = () => {
    const location = useLocation();

    useEffect(() => {
        const page = `${window.location.hostname} ${location.pathname}${location.search}`;
        ReactGA.send({
            hitType: 'pageview',
            page: page,
            title: page
        });
    }, [location]);

    return null;
};

const MobileBottomNavWithScroll = () => {
    const { width } = useDimensions();
    const location = useLocation();
    const { mobileBottomNavIsVisible, setMobileBottomNavIsVisible } = useMainNav();

    useEffect(() => {
        let lastScrollY = window.scrollY;
        const threshold = 250;

        const handleScroll = () => {
            if (window.scrollY < threshold) {
                setMobileBottomNavIsVisible(true);
                lastScrollY = window.scrollY;
                return;
            }

            if (window.scrollY > lastScrollY) {
                setMobileBottomNavIsVisible(false);
            } else {
                setMobileBottomNavIsVisible(true);
            }

            lastScrollY = window.scrollY;
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [location.pathname, setMobileBottomNavIsVisible]);

    useEffect(() => {
        setMobileBottomNavIsVisible(true);
    }, [location.pathname, setMobileBottomNavIsVisible]);

    return (
        <>
            <ScrollRestoration getKey={(currentLocation) => currentLocation.pathname} />
            <div id='body' className='municipality-detail-view'>
                <ScrollToTop/>
                <Outlet/>
            </div>
            <MobileBottomNav isVisible={mobileBottomNavIsVisible && width < 768} />
        </>
    );
};

const AppLayout = () => {
    const [latestMeetingAt, setLatestMeetingAt] = useState<string>("");
    const [latestMeetingNumber, setLatestMeetingNumber] = useState<string>("");
    const [municipalityTimeZone, setMunicipalityTimeZone] = useState<string>("");

    return (
        <>
            <Analytics />
            <FooterContext.Provider value={{ setLatestMeetingAt, setLatestMeetingNumber, setMunicipalityTimeZone }}>
                <LocalizationLabelsProvider>
                    <div className="app-shell">
                        <MainNavProvider>
                            <NavMenu />
                            { PAGE_WARNING ? <Message severity='warn' text={PAGE_WARNING} /> : <></> }
                            <MobileBottomNavWithScroll />
                        </MainNavProvider>
                        <Footer
                            latestMeetingAt={latestMeetingAt}
                            latestMeetingNumber={latestMeetingNumber}
                            timeZone={municipalityTimeZone}
                        />
                    </div>
                </LocalizationLabelsProvider>
            </FooterContext.Provider>

        </>
    );
};

export default AppLayout;
export {FooterContext};

